import { useMemo, useState, useRef, useEffect } from 'react';
import { ProjectDtoRelV2 } from '../../../commonTypes/project.types';
import { useNavigate } from 'react-router';
import { DateTime } from 'luxon';
import { hexToRgba } from '../../../util/colourHelper';

interface ProjectsCalendarViewProps {
    projects: ProjectDtoRelV2[];
    searchTerm: string;
    selectedFilters: string[];
}

interface ProjectBar {
    id: string;
    name: string;
    clientName?: string;
    status: string;
    startDate: DateTime;
    endDate: DateTime;
    row: number;
}

const STATUS_COLORS_BG: Record<string, string> = {
    KickOff: '#FCF7FF',
    InProgress: '#F5F9FF',
    OnHold: '#FFFBF0',
    Cancelled: '#FFF6F6',
    Completed: '#EFFFF7',
};

const STATUS_COLORS_BORDER: Record<string, string> = {
    KickOff: '#af52de',
    InProgress: '#4b99f5',
    OnHold: '#e5a000',
    Cancelled: '#eb6060',
    Completed: '#14ae5c',
};

const STATUS_LABELS: Record<string, string> = {
    KickOff: 'Kick Off',
    InProgress: 'In Progress',
    OnHold: 'On Hold',
    Cancelled: 'Cancelled',
    Completed: 'Completed',
};

export const ProjectsCalendarView = ({ projects, searchTerm, selectedFilters }: ProjectsCalendarViewProps) => {
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement>(null);
    const now = DateTime.now();
    const [visibleYear, setVisibleYear] = useState(now.year);
    const currentYear = now.year;
    const currentMonth = now.month;
    const [yearRange, setYearRange] = useState({ min: currentYear - 5, max: currentYear + 5 });
    const previousYearRangeRef = useRef(yearRange);

    const filteredProjects = useMemo(() => {
        const q = searchTerm.toLowerCase().trim();

        let result = !q
            ? [...projects]
            : projects.filter(p => {
                  const n = p.name?.toLowerCase() ?? '';
                  const c = p.clientName?.toLowerCase() ?? '';
                  return n.includes(q) || c.includes(q);
              });

        if (selectedFilters.length) {
            result = result.filter(p => selectedFilters.includes(p.projectStatus || (p.active ? 'InProgress' : 'Draft')));
        }

        return result.filter(p => p.startDate);
    }, [projects, searchTerm, selectedFilters]);

    const years = useMemo(() => {
        const yearList = [];
        for (let y = yearRange.min; y <= yearRange.max; y++) {
            yearList.push(y);
        }
        return yearList;
    }, [yearRange]);

    const monthColumns = useMemo(() => {
        const columns: { year: number; month: number; date: DateTime }[] = [];
        years.forEach(year => {
            for (let month = 1; month <= 12; month++) {
                columns.push({
                    year,
                    month,
                    date: DateTime.local(year, month, 1),
                });
            }
        });
        return columns;
    }, [years]);

    const projectBars = useMemo(() => {
        const bars: ProjectBar[] = [];
        const projectsWithDates = filteredProjects.map(p => ({
            id: p.id,
            name: p.name,
            clientName: p.clientName,
            status: p.projectStatus || 'InProgress',
            start: DateTime.fromISO(p.startDate!),
            end: p.endDate ? DateTime.fromISO(p.endDate) : DateTime.now().plus({ years: 100 }),
        }));

        projectsWithDates.sort((a, b) => a.start.toMillis() - b.start.toMillis());

        const rowEndPositions: number[] = [];

        projectsWithDates.forEach(project => {
            const startMillis = project.start.toMillis();
            const endMillis = project.end.toMillis();

            let assignedRow = -1;
            for (let row = 0; row < rowEndPositions.length; row++) {
                if (rowEndPositions[row] <= startMillis) {
                    assignedRow = row;
                    rowEndPositions[row] = endMillis;
                    break;
                }
            }

            if (assignedRow === -1) {
                assignedRow = rowEndPositions.length;
                rowEndPositions.push(endMillis);
            }

            bars.push({
                id: project.id,
                name: project.name,
                clientName: project.clientName,
                status: project.status,
                startDate: project.start,
                endDate: project.end,
                row: assignedRow + 1,
            });
        });

        return bars;
    }, [filteredProjects]);

    useEffect(() => {
        if (containerRef.current && monthColumns.length > 0) {
            const currentYearJanIndex = monthColumns.findIndex(col => col.year === currentYear && col.month === 1);
            if (currentYearJanIndex >= 0) {
                const scrollPosition = currentYearJanIndex * 110;
                setTimeout(() => {
                    if (containerRef.current) {
                        containerRef.current.scrollLeft = scrollPosition;
                    }
                }, 100);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!containerRef.current) return;

        const prevRange = previousYearRangeRef.current;
        const currentRange = yearRange;

        if (prevRange.min > currentRange.min) {
            const yearsAdded = prevRange.min - currentRange.min;
            const scrollAdjustment = yearsAdded * 12 * 110;
            containerRef.current.scrollLeft += scrollAdjustment;
        }

        previousYearRangeRef.current = currentRange;
    }, [yearRange]);

    const scrollToYear = (year: number) => {
        if (!containerRef.current || monthColumns.length === 0) return;

        const targetYearJanIndex = monthColumns.findIndex(col => col.year === year && col.month === 1);

        if (targetYearJanIndex >= 0) {
            const scrollPosition = targetYearJanIndex * 110;
            containerRef.current.scrollTo({
                left: scrollPosition,
                behavior: 'smooth',
            });
            setVisibleYear(year);
        }
    };

    const handlePreviousYear = () => {
        scrollToYear(visibleYear - 1);
    };

    const handleNextYear = () => {
        scrollToYear(visibleYear + 1);
    };

    const handleScroll = () => {
        if (!containerRef.current) return;

        const scrollLeft = containerRef.current.scrollLeft;
        const scrollWidth = containerRef.current.scrollWidth;
        const clientWidth = containerRef.current.clientWidth;

        const centerPosition = scrollLeft + clientWidth / 2;
        const columnIndex = Math.floor(centerPosition / 110);

        if (columnIndex >= 0 && columnIndex < monthColumns.length) {
            const visibleCol = monthColumns[columnIndex];
            if (visibleCol && visibleCol.year !== visibleYear) {
                setVisibleYear(visibleCol.year);
            }
        }

        const scrollThreshold = 1320;

        if (scrollLeft < scrollThreshold) {
            setYearRange(prev => {
                const newMin = prev.min - 3;
                return { ...prev, min: newMin };
            });
        }

        if (scrollLeft + clientWidth > scrollWidth - scrollThreshold) {
            setYearRange(prev => {
                const newMax = prev.max + 3;
                return { ...prev, max: newMax };
            });
        }
    };

    const getBarStyle = (bar: ProjectBar) => {
        const startMonthIndex = monthColumns.findIndex(col => col.year === bar.startDate.year && col.month === bar.startDate.month);
        const endMonthIndex = monthColumns.findIndex(col => col.year === bar.endDate.year && col.month === bar.endDate.month);

        const actualEndIndex = endMonthIndex >= 0 ? endMonthIndex : monthColumns.length - 1;

        const backgroundColor = STATUS_COLORS_BG[bar.status];
        const borderColor = STATUS_COLORS_BORDER[bar.status];

        return {
            gridColumnStart: startMonthIndex + 1,
            gridColumnEnd: actualEndIndex + 2,
            gridRow: bar.row,
            backgroundColor: backgroundColor,
            borderColor: borderColor,
        };
    };

    return (
        <div className="projects-yearly-calendar">
            <div className="projects-yearly-calendar__toolbar">
                <button className="projects-yearly-calendar__year-nav" onClick={handlePreviousYear} aria-label="Previous year">
                    <img src="img/common/arrow-left.svg" alt="Previous year" />
                </button>
                <h3>{visibleYear}</h3>
                <button className="projects-yearly-calendar__year-nav" onClick={handleNextYear} aria-label="Next year">
                    <img src="img/common/arrow-right.svg" alt="Next year" />
                </button>
            </div>

            <div ref={containerRef} className="projects-yearly-calendar__scroll-container" onScroll={handleScroll}>
                <div
                    className="projects-yearly-calendar__grid"
                    style={{
                        gridTemplateColumns: `repeat(${monthColumns.length}, 110px)`,
                    }}
                >
                    <div className="projects-yearly-calendar__months" style={{ gridColumn: `1 / -1` }}>
                        {monthColumns.map(col => {
                            const isCurrentMonth = col.year === currentYear && col.month === currentMonth;
                            return (
                                <div
                                    key={`month-${col.year}-${col.month}`}
                                    className={`projects-yearly-calendar__month ${isCurrentMonth ? 'projects-yearly-calendar__month--current' : ''}`}
                                >
                                    {col.date.toFormat('MMMM')}
                                </div>
                            );
                        })}
                    </div>

                    <div
                        className="projects-yearly-calendar__timeline"
                        style={{
                            gridColumn: `1 / -1`,
                            gridTemplateColumns: `repeat(${monthColumns.length}, 110px)`,
                        }}
                    >
                        {projectBars.map(bar => {
                            const barStyle = getBarStyle(bar);
                            const borderColor = hexToRgba(barStyle.borderColor as string, 0.1);
                            return (
                                <div
                                    key={bar.id}
                                    className="projects-yearly-calendar__project-bar"
                                    style={{
                                        ...barStyle,
                                        border: `1px solid ${borderColor}`,
                                    }}
                                    onClick={() => navigate(`/project/${bar.id}`)}
                                >
                                    <div
                                        className="projects-yearly-calendar__project-sticky-wrapper"
                                        style={{
                                            backgroundColor: barStyle.backgroundColor,
                                            borderLeft: `4px solid ${barStyle.borderColor}`,
                                            borderRadius: '2px',
                                        }}
                                    >
                                        <div className="projects-yearly-calendar__project-content">
                                            <div className="projects-yearly-calendar__project-name">{bar.name}</div>
                                            {bar.clientName && <div className="projects-yearly-calendar__project-client">{bar.clientName}</div>}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="projects-yearly-calendar__legend-wrapper">
                <div className="projects-yearly-calendar__legend">
                    {Object.entries(STATUS_LABELS).map(([status, label]) => (
                        <div key={status} className="projects-yearly-calendar__legend-item">
                            <div className="projects-yearly-calendar__legend-color" style={{ backgroundColor: STATUS_COLORS_BORDER[status] }} />
                            <span>{label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
